import { Card, CardContent } from '@/components/ui/card'
import React from 'react'
import Radio from '@/features/surveys/components/preview-modal-template/Radio'
import MultiSelect from '@/features/surveys/components/preview-modal-template/MultiSelect'
import RatingScale from '@/features/surveys/components/preview-modal-template/RatingScale'
import NpsScale from '@/features/surveys/components/preview-modal-template/NpsScale'
import Dropdown from '@/features/surveys/components/preview-modal-template/Dropdown'
import SingleSelectImage from '@/features/surveys/components/preview-modal-template/SingleSelectImage'
import ContentBlock from '@/features/surveys/components/preview-modal-template/ContentBlock'
import Text from '@/features/surveys/components/preview-modal-template/Text'
import Date from '@/features/surveys/components/preview-modal-template/Date'

// Helper function to add static URL prefix to image URLs for specific template
const addStaticUrlToImageQuestions = (
    questions: any[],
    surveyId: string | null | undefined
) => {
    const TARGET_TEMPLATE_ID = 'eb4853bb-694f-4c12-8686-5b1bbf83ab17'
    const imageUrl1 = '/builder/pack1.png'
    const imageUrl2 = '/builder/pack2.png'

    // Early return if surveyId is null, undefined, or doesn't match target
    if (!surveyId || surveyId !== TARGET_TEMPLATE_ID) {
        return questions
    }

    // Early return if questions is not an array
    if (!Array.isArray(questions)) {
        return questions
    }

    return questions.map((question: any) => {
        if (
            question?.type === 'image' &&
            question?.config?.options &&
            Array.isArray(question.config.options)
        ) {
            return {
                ...question,
                config: {
                    ...question.config,
                    options: question.config.options.map(
                        (option: any, index: number) => ({
                            ...option,
                            imageUrl: index === 0 ? imageUrl1 : imageUrl2,
                        })
                    ),
                },
            }
        }
        return question
    })
}

interface ModalTemplateCardProps {
    card: {
        categoryTag: string
        name: string
        description: string
        questionsCount: number
        surveyId: string
        estimatedTimeBreakdown: {
            minutes: number
            seconds: number
        }
        // Optional questions array for richer previews
        questions?: Array<any>
    }
    onClick: (card: any) => void
}

const ModalTemplateCard = ({ card, onClick }: ModalTemplateCardProps) => {
    const [isHovering, setIsHovering] = React.useState(false)
    const [previewIndex, setPreviewIndex] = React.useState(0)

    const questions = React.useMemo(() => {
        const originalQuestions = card.questions || []
        // Process questions to add static URL prefix for specific template
        return addStaticUrlToImageQuestions(originalQuestions, card.surveyId)
    }, [card.questions, card.surveyId])

    React.useEffect(() => {
        if (!isHovering || questions.length === 0) return
        const interval = setInterval(() => {
            setPreviewIndex((prev) => (prev + 1) % questions.length)
        }, 1500)
        return () => clearInterval(interval)
    }, [isHovering, questions.length])

    const currentQuestion = questions[previewIndex]

    const renderStaticPreview = () => {
        if (!currentQuestion) {
            return (
                <div className="w-full h-full rounded-t-lg bg-gray-50 flex items-center justify-center">
                    <span className="text-xs text-gray-400">
                        Question preview
                    </span>
                </div>
            )
        }

        const type = currentQuestion.type
        const questionText =
            currentQuestion.text || 'Your question text comes here..'

        return (
            <div className="w-full h-full">
                <div className="w-full h-full flex flex-col items-start justify-center  gap-2 p-3 bg-[#F6F6F6]">
                    <div className="scale-[0.85]  w-full">
                        <div className="text-[13px] font-[550] text-[#303030] line-clamp-2">
                            {questionText}
                        </div>
                        <div
                            className="w-full"
                            key={`${currentQuestion?.id}-auto-preview`}
                        >
                            {type === 'single_select' ? (
                                <Radio
                                    questionId={currentQuestion.id}
                                    options={(
                                        currentQuestion?.config?.options || []
                                    ).slice(0, 3)}
                                    allowOther={false}
                                    otherLabel={
                                        currentQuestion?.config?.otherLabel
                                    }
                                    displayType={
                                        currentQuestion?.config?.displayType
                                    }
                                    selectedOptionId={undefined}
                                    otherValue={''}
                                />
                            ) : currentQuestion?.type === 'multi_select' ? (
                                <MultiSelect
                                    questionId={currentQuestion.id}
                                    options={(
                                        currentQuestion?.config?.options || []
                                    ).slice(0, 3)}
                                    allowOther={false}
                                    otherLabel={
                                        currentQuestion?.config?.otherLabel
                                    }
                                    selectedOptionIds={[]}
                                    otherValue={''}
                                />
                            ) : currentQuestion?.type === 'text' ? (
                                <Text
                                    questionId={currentQuestion.id}
                                    placeholder={
                                        currentQuestion?.config?.placeholder
                                    }
                                    value={''}
                                />
                            ) : currentQuestion?.type === 'rating' ? (
                                <RatingScale
                                    displayType={
                                        currentQuestion?.config?.displayType
                                    }
                                    minLabel={currentQuestion?.config?.minLabel}
                                    maxLabel={currentQuestion?.config?.maxLabel}
                                    selectedValue={undefined}
                                />
                            ) : currentQuestion?.type === 'nps' ? (
                                <NpsScale
                                    displayType={
                                        currentQuestion?.config?.displayType
                                    }
                                    minLabel={currentQuestion?.config?.minLabel}
                                    maxLabel={currentQuestion?.config?.maxLabel}
                                    selectedValue={undefined}
                                />
                            ) : currentQuestion?.type === 'drop_down' ? (
                                <Dropdown
                                    questionId={currentQuestion.id}
                                    options={(
                                        currentQuestion?.config?.options || []
                                    ).slice(0, 3)}
                                    allowOther={false}
                                    selectedOptionId={undefined}
                                />
                            ) : currentQuestion?.type === 'date' ? (
                                <Date
                                    questionId={currentQuestion.id}
                                    selectedValue={undefined}
                                />
                            ) : currentQuestion?.type === 'image' ? (
                                <SingleSelectImage
                                    questionId={currentQuestion.id}
                                    options={(
                                        currentQuestion?.config?.options || []
                                    ).slice(0, 2)}
                                    hideLabel={
                                        currentQuestion?.config?.hideLabel
                                    }
                                    selectedOptionId={undefined}
                                />
                            ) : currentQuestion?.type === 'content_block' ? (
                                <ContentBlock
                                    questionId={currentQuestion.id}
                                    contentBlocks={
                                        currentQuestion?.contentBlocks
                                    }
                                />
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <Card
            className="w-full h-[286px] shadow-none  cursor-pointer bg-white overflow-hidden border-none rounded-lg"
            onClick={() => onClick(card)}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => {
                setIsHovering(false)
                setPreviewIndex(0)
            }}
        >
            <CardContent className="p-0 h-full flex flex-col border !border-[#E5E5E5] !rounded-lg overflow-hidden">
                {/* Question Preview */}
                <div className="flex-1 overflow-hidden">
                    {renderStaticPreview()}
                </div>

                {/* Template Info */}
                <div className="bg-white p-4">
                    <h4 className="text-[16px] font-[650] text-[#303030] mb-1 line-clamp-1">
                        {card.name}
                    </h4>
                    <p className="text-[12px] font-[450] text-[#616161] line-clamp-2">
                        {card.description}
                    </p>
                </div>
            </CardContent>
        </Card>
    )
}

export default ModalTemplateCard
